// The departure board on /ielts/calculator.
//
// ⛔ No React and no arithmetic. The server has already rendered a complete
// board for the fader's starting position, and every other position is in the
// page as data — so this file finds elements, looks a row up, and turns the
// leaves over. With scripting off the board is still a whole answer.
//
// ⛔ Nothing here runs on a timer. The leaves turn when new information
// arrives — the reader moved the fader or flipped the schedule — which is what
// a real board does, and it is the reason this world can be built at all: a
// countdown is a copy red line on this product.

import { setupCurveBuilder } from 'utils/ieltsCurve'
import { selectVariant, formatNumber, flapDelays, changedIndexes, fitToLeaves } from 'utils/ieltsPlan'

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Turn one value's leaves over to a new value. Only the characters that
// actually changed move; the rest are already right.
function turnFlaps (node, next) {
  const tiles = Array.from(node.querySelectorAll('.brd-flap'))
  const before = tiles.map((tile) => tile.textContent).join('')
  const after = fitToLeaves(next, tiles.length)

  if (before === after) return

  // The spoken value travels beside the tiles and changes with them; the tiles
  // themselves are hidden from assistive tech.
  const spoken = node.querySelector('[data-ielts-flap-value]')
  if (spoken) spoken.textContent = after

  // The leaf count is fixed by the server render, so a longer value writes
  // straight in rather than growing the row mid-turn.
  if (tiles.length !== after.length) {
    tiles.forEach((tile, index) => { tile.textContent = after[index] ?? '' })
    return
  }

  const moving = changedIndexes(before, after)

  if (reduceMotion()) {
    moving.forEach((index) => { tiles[index].textContent = after[index] })
    return
  }

  const delays = flapDelays(tiles.length)

  moving.forEach((index) => {
    const tile = tiles[index]

    window.setTimeout(() => {
      tile.dataset.flipping = ''
      // Swap the character at the halfway point, while the leaf is edge-on.
      window.setTimeout(() => { tile.textContent = after[index] }, 130)
      tile.addEventListener('animationend', () => { delete tile.dataset.flipping }, { once: true })
    }, delays[index])
  })
}

function setup () {
  const root = document.querySelector('[data-ielts-variants]')
  if (!root) return

  let variants = []
  try {
    variants = JSON.parse(root.dataset.ieltsVariants)
  } catch (error) {
    // A payload we cannot read leaves the server-rendered board exactly as it is.
    return
  }

  const slider = document.querySelector('[data-ielts-daily]')
  if (!slider) return

  const schedules = Array.from(document.querySelectorAll('[data-ielts-schedule]'))
  const fields = Array.from(document.querySelectorAll('[data-ielts-field]'))
  const capNote = document.querySelector('[data-ielts-cap-note]')
  const dailyLabel = document.querySelector('[data-ielts-daily-label]')

  const currentSchedule = () => {
    const checked = schedules.find((input) => input.checked)
    return checked ? checked.value : root.dataset.ieltsDefaultSchedule
  }

  const apply = (settled) => {
    const variant = selectVariant(variants, currentSchedule(), Number(slider.value))
    if (!variant) return

    fields.forEach((node) => {
      const value = variant[node.dataset.ieltsField]
      // ⛔ A field the row has no value for keeps what the server rendered. The
      // board never shows a blank where a number was.
      if (value === null || value === undefined) return

      const next = formatNumber(value)
      const leaves = node.querySelector('.brd-flap')

      if (!leaves) { node.textContent = next; return }
      // ⛔ Leaves turn on `change`, not on `input`. A board that re-turns on
      // every drag pixel is a thing that never stops moving, which is the
      // countdown affordance this product forbids arriving by the back door.
      if (settled) turnFlaps(node, next)
    })

    if (dailyLabel) dailyLabel.textContent = formatNumber(slider.value)

    if (capNote && capNote.dataset.ieltsCapTemplate) {
      // ⛔ Shown exactly when something was discounted. Always saying "you
      // entered 3, we count 3" teaches the reader to skip the note; never
      // saying it lets the discount happen silently.
      const discounted = Number(slider.value) > Number(variant.effectiveDaily)
      capNote.hidden = !discounted

      if (discounted) {
        capNote.textContent = capNote.dataset.ieltsCapTemplate
          .replaceAll('__entered__', formatNumber(slider.value))
          .replaceAll('__effective__', formatNumber(variant.effectiveDaily))
      }
    }
  }

  // The readout follows the thumb; the leaves turn when the reader lets go.
  slider.addEventListener('input', () => apply(false))
  slider.addEventListener('change', () => apply(true))
  schedules.forEach((input) => input.addEventListener('change', () => apply(true)))
  apply(false)
}

// ⛔ The builder mounts on its own, not inside `setup`. `setup` returns early
// when there is no fader, and there is no fader until the reader has entered a
// combination — but the section that explains the figures is on the page from
// the first paint, and that is where the builder lives.
// Says so the moment both choices are made, rather than after a page load.
//
// ⛔ It does not block the submit and it does not open anything. The reader has
// made a coherent choice with a correct answer, and someone who types the same
// address gets that answer either way — so the note is a heads-up, not a gate.
function setupAboveTargetNote () {
  const note = document.querySelector('[data-ielts-above-target]')
  const target = document.querySelector('select[name="target"]')
  const level = document.querySelector('select[name="level"]')
  if (!note || !target || !level) return

  // The level options carry their band in the token, e.g. `band-4.5`. Anything
  // else (a TOEIC score, a self-assessment) is read off the same prefix.
  const bandOf = (value) => {
    const match = String(value).match(/(\d+(?:\.\d+)?)/)
    return match ? Number(match[1]) : null
  }

  const update = () => {
    // ⛔ An unchosen option is not a zero. `Number('')` is 0 and passes
    // isFinite, so resetting the target to the blank option made the note claim
    // the reader was already there.
    const chosen = target.value === '' ? null : Number(target.value)
    const at = bandOf(level.value)

    note.hidden = !(chosen !== null && Number.isFinite(chosen) && at !== null && chosen <= at)
  }

  target.addEventListener('change', update)
  level.addEventListener('change', update)
}

function boot () {
  setup()
  setupCurveBuilder()
  setupAboveTargetNote()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot)
} else {
  boot()
}
